import React, { useState } from 'react';
import { FiSend } from 'react-icons/fi';
import { unsubscribe } from '../api';
import { showToast } from '../toaster';

const reasons = [
  'I do not understand the amount on my bank statement',
  'I wish to receive the invoice of my order',
  'I wish to cancel',
  'I want to unsubscribe',
  'Other',
];

const isValidEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());

const isValidIbanLastFive = (value: string) =>
  /^\d{5}$/.test(value.trim());

export default function ContactForm() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [iban, setIban] = useState('');
  const [reasonIdx, setReasonIdx] = useState(0);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setIban('');
    setReasonIdx(0);
    setMessage('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    const trimmedIban = iban.trim();

    if (!isValidEmail(trimmedEmail)) {
      showToast('Please enter a valid email address.', 'error');
      return;
    }

    if (!isValidIbanLastFive(trimmedIban)) {
      showToast('Please enter exactly the last 5 digits of your IBAN.', 'error');
      return;
    }

    setLoading(true);
    const isUnsub = reasonIdx === 2 || reasonIdx === 3;
    try {
      await unsubscribe({
        first_name: firstName,
        last_name: lastName,
        email: trimmedEmail,
        iban_last_5: trimmedIban,
        is_unsubscribe: isUnsub,
        message,
        options: { reason: reasons[reasonIdx] },
      });
      showToast(
        isUnsub ? 'Unsubscribe request sent successfully.' : 'Message sent successfully.',
        'success'
      );
      reset();
    } catch (err: any) {
      let msg = err?.message || 'Something went wrong.';
      // If error is JSON, try to extract message
      try {
        const parsed = JSON.parse(msg);
        if (parsed && typeof parsed.message === 'string') msg = parsed.message;
      } catch {}
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      <div className="mb-6 flex flex-col gap-3 border-b border-emerald-900/10 pb-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#0F766E]">Request form</p>
          <h3 className="mt-2 text-3xl font-semibold tracking-tight text-[#0B2F2A]">Contact us</h3>
        </div>
        <p className="max-w-sm text-sm leading-6 text-[#56736B]">Our operators will answer your questions quickly and efficiently.</p>
      </div>
      <form className="space-y-5 text-sm" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <input
            aria-label="First name"
            placeholder="First name *"
            className="w-full rounded-lg border border-emerald-900/15 bg-[#F4FBF8] px-4 py-4 text-[#0B2F2A] placeholder:text-[#81968F] transition focus:border-[#0F766E] focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-100"
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            required
          />
          <input
            aria-label="Last name"
            placeholder="Last name *"
            className="w-full rounded-lg border border-emerald-900/15 bg-[#F4FBF8] px-4 py-4 text-[#0B2F2A] placeholder:text-[#81968F] transition focus:border-[#0F766E] focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-100"
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            required
          />
        </div>
        <input
          type="email"
          aria-label="Email"
          placeholder="Email *"
          className="w-full rounded-lg border border-emerald-900/15 bg-[#F4FBF8] px-4 py-4 text-[#0B2F2A] placeholder:text-[#81968F] transition focus:border-[#0F766E] focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-100"
          value={email}
          onChange={e => setEmail(e.target.value)}
          autoComplete="email"
          required
        />
        <input
          aria-label="IBAN"
          placeholder="Last 5 digits of IBAN *"
          className="w-full rounded-lg border border-emerald-900/15 bg-[#F4FBF8] px-4 py-4 text-[#0B2F2A] placeholder:text-[#81968F] transition focus:border-[#0F766E] focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-100"
          value={iban}
          onChange={e => setIban(e.target.value.replace(/\D/g, '').slice(0, 5))}
          inputMode="numeric"
          pattern="[0-9]{5}"
          title="Enter exactly the last 5 digits of your IBAN"
          required
          maxLength={5}
        />
        <select
          className="w-full rounded-lg border border-emerald-900/15 bg-[#F4FBF8] px-4 py-4 text-[#0B2F2A] transition focus:border-[#0F766E] focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-100"
          value={reasonIdx}
          onChange={e => setReasonIdx(Number(e.target.value))}
        >
          {reasons.map((r, i) => (
            <option value={i} key={r}>{`${i + 1} - ${r}`}</option>
          ))}
        </select>
        <textarea
          aria-label="Message"
          placeholder="Your message *"
          className="h-32 w-full resize-none rounded-lg border border-emerald-900/15 bg-[#F4FBF8] px-4 py-4 text-[#0B2F2A] placeholder:text-[#81968F] transition focus:border-[#0F766E] focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-100"
          value={message}
          onChange={e => setMessage(e.target.value)}
          required
        />
        <button
          type="submit"
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#D97706] py-4 text-base font-semibold text-white shadow-lg shadow-amber-900/15 transition-colors hover:bg-[#B45309] disabled:opacity-60"
          disabled={loading}
        >
          <FiSend className="w-4 h-4" /> {loading ? 'SENDING...' : 'SEND MESSAGE'}
        </button>
      </form>
    </div>
  );
}
